import { useState, useCallback, useRef } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { APOLLO_CLIENT_FETCH_POLICY_NO_CACHE } from '../../../../../services/apollo-graphql/index'
import {
  ALCHEMIZE_PLAYLIST_QUERY,
  PLAYLIST_CREATE_WITH_ITEMS_MUTATION,
} from '../../../../../services/community/oracle/alchemize-playlist-queries'

// Per the Community service convention, the gql operations for this flow live
// alongside the other oracle-domain documents in
// lib/services/community/oracle/alchemize-playlist-queries.ts. Response shape
// interfaces stay co-located with the hook below (only consumed here).

// ── Types ──

interface ContentAssetRef {
  asset?: {
    renderUrl?: string | null
    language?: string | null
  } | null
}

/**
 * Shape consumed by the modal's tile renderer. Preserved from the previous
 * oracle hook so the renderer is untouched — alchemize results are mapped
 * into this shape at the hook boundary (see `videoToDocument`).
 */
export interface PlaylistDocument {
  componentId: string
  contentId: number
  title: string
  contentType: string
  path?: string | null
  durationSeconds?: number | null
  instructor?: string | null
  seriesTitle?: string | null
  content?: {
    id?: string | null
    contentId?: number | null
    path?: string | null
    title?: string | null
    contentType?: string | null
    contentAssetReferences?: ContentAssetRef[] | null
    featureMedia?: { mediaId?: string | null } | null
  } | null
}

interface AlchemizeVideo {
  id: string
  contentId: number
  contentType: string
  title?: string | null
  shortDescription?: string | null
  durationSeconds?: number | null
  instructor?: string | null
  seriesTitle?: string | null
  episodeNumber?: number | null
  seasonNumber?: number | null
  thumbnailUrl?: string | null
  path?: string | null
}

interface AlchemizePlaylistResponse {
  alchemizePlaylist: {
    videos: AlchemizeVideo[]
    narrative?: string | null
    suggestedName?: string | null
  }
}

interface PlaylistCreateWithItemsResponseShape {
  playlistCreateWithItems: {
    playlist: {
      id: string
      uid?: string | null
      playlistType?: string | null
      playlistName?: string | null
    } | null
    playlistItems: Array<{
      id: string
      contentId: number
      playlistPosition?: number | null
    } | null> | null
  }
}

export interface GenerateOptions {
  targetDurationMinutes?: number
  maxVideos?: number
}

export interface SaveResult {
  success: boolean
  playlistId: string | null
  playlistName: string | null
  itemsAdded: number
  errors?: string[]
}

// ── Mapping ──

function videoToDocument (v: AlchemizeVideo): PlaylistDocument {
  // Synthetic single asset-ref so getDocumentThumbnail() — which expects the
  // legacy oracle shape — keeps working without renderer changes.
  const refs: ContentAssetRef[] = v.thumbnailUrl
    ? [{ asset: { renderUrl: v.thumbnailUrl, language: 'en' } }]
    : []
  return {
    componentId: v.id,
    contentId: v.contentId,
    title: v.title ?? '',
    contentType: v.contentType,
    path: v.path ?? null,
    durationSeconds: v.durationSeconds ?? null,
    instructor: v.instructor ?? null,
    seriesTitle: v.seriesTitle ?? null,
    content: {
      id: v.id,
      contentId: v.contentId,
      path: v.path ?? null,
      title: v.title ?? null,
      contentType: v.contentType,
      contentAssetReferences: refs,
    },
  }
}

// ── Hook ──

export interface UsePlaylistGenerationReturn {
  isGenerating: boolean
  isSaving: boolean
  narrativeText: string
  documents: PlaylistDocument[]
  suggestedName: string | null
  error: string | null
  generate: (prompt: string, options?: GenerateOptions) => void
  reroll: (prompt: string, options?: GenerateOptions) => void
  save: (
    playlistName: string,
    description: string,
    items: Array<{ contentId: number; contentType: string }>,
  ) => Promise<SaveResult | null>
  reset: () => void
}

export function usePlaylistGeneration (
  jwt: string,
  language?: string,
): UsePlaylistGenerationReturn {
  const apolloClient = useApolloClient()
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [narrativeText, setNarrativeText] = useState('')
  const [documents, setDocuments] = useState<PlaylistDocument[]>([])
  const [suggestedName, setSuggestedName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Monotonically-increasing request id. Each `executeGenerate` invocation
  // captures the id assigned at the top of its scope, and any state write
  // is gated on the captured id still matching `current`. Apollo doesn't
  // auto-cancel in-flight `query` calls, so without this guard a stale
  // response (slow first request, fast retry) would clobber the live one —
  // setIsGenerating(false) from the stale resolve also trips the modal's
  // generating→results transition with the wrong videos.
  // See plan `do-a-code-review-eager-twilight.md` (finding #5).
  const requestIdRef = useRef(0)

  const executeGenerate = useCallback(async (
    prompt: string,
    options?: GenerateOptions,
  ) => {
    requestIdRef.current += 1
    const myRequestId = requestIdRef.current
    setNarrativeText('')
    setSuggestedName(null)
    // Keep `documents` populated so the modal's ghost layout doesn't collapse
    // during the generating transition. Cleared on `reset()` or next success.
    setError(null)
    setIsGenerating(true)

    const input: {
      prompt: string
      language?: string
      targetDurationMinutes?: number
      maxVideos?: number
    } = { prompt }
    if (language) input.language = language
    if (options?.targetDurationMinutes !== undefined) {
      input.targetDurationMinutes = options.targetDurationMinutes
    }
    if (options?.maxVideos !== undefined) {
      input.maxVideos = options.maxVideos
    }

    try {
      const result = await apolloClient.query<AlchemizePlaylistResponse>({
        query: ALCHEMIZE_PLAYLIST_QUERY,
        variables: { input },
        fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NO_CACHE,
        context: { jwt },
      })
      // Stale response: a newer generate (or reset) has superseded this one.
      // Drop its data on the floor — do NOT touch isGenerating / error /
      // documents, since those belong to the still-pending newer call.
      if (myRequestId !== requestIdRef.current) return

      const data = result.data?.alchemizePlaylist
      if (!data) {
        setError('No response from playlist generator.')
        setIsGenerating(false)
        return
      }

      if (!data.videos || data.videos.length === 0) {
        setError('No recommendations were returned. Try a different prompt.')
        setIsGenerating(false)
        return
      }

      setNarrativeText(data.narrative ?? '')
      setSuggestedName(data.suggestedName ?? null)
      setDocuments(data.videos.map(videoToDocument))
    } catch (e) {
      if (myRequestId !== requestIdRef.current) return
      const msg = e instanceof Error ? e.message : 'Playlist generation failed'
      setError(msg)
    }
    // Only the latest request may clear the generating flag — otherwise a
    // stale resolve would flip the UI to "results" while a newer request is
    // still pending.
    if (myRequestId === requestIdRef.current) setIsGenerating(false)
  }, [apolloClient, jwt, language])

  const generate = useCallback((
    prompt: string,
    options?: GenerateOptions,
  ) => {
    executeGenerate(prompt, options)
  }, [executeGenerate])

  // reroll = same prompt + options, fresh request. Same function reference;
  // the modal's caller always passes the latest prompt/options it already
  // has in component state, so we don't need to capture them in a ref.
  const reroll = generate

  const [createWithItemsMutation] = useMutation<PlaylistCreateWithItemsResponseShape>(
    PLAYLIST_CREATE_WITH_ITEMS_MUTATION,
    { context: { jwt } },
  )

  const save = useCallback(async (
    playlistName: string,
    description: string,
    items: Array<{ contentId: number; contentType: string }>,
  ): Promise<SaveResult | null> => {
    setIsSaving(true)
    setError(null)
    try {
      const result = await createWithItemsMutation({
        variables: {
          playlist: {
            playlistType: 'user',
            playlistName,
            description: description || null,
          },
          // Backend reads `items` in array order — first element becomes the
          // first playlist position. The Alchemize response delivers videos
          // in the LLM's recommended order, so we pass straight through.
          items: items.map((it) => ({
            contentId: it.contentId,
            contentType: it.contentType,
          })),
        },
      })

      const data = result.data?.playlistCreateWithItems
      const playlist = data?.playlist
      if (!playlist?.id) {
        setError('Failed to create playlist')
        return {
          success: false, playlistId: null, playlistName: null, itemsAdded: 0,
        }
      }

      const itemsAdded = data?.playlistItems?.filter(Boolean).length ?? 0
      return {
        success: true,
        playlistId: playlist.id,
        playlistName: playlist.playlistName ?? playlistName,
        itemsAdded,
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save playlist'
      setError(msg)
      return null
    } finally {
      setIsSaving(false)
    }
  }, [createWithItemsMutation])

  const reset = useCallback(() => {
    // Bump the request id so any in-flight generate's eventual resolve is
    // treated as stale and skips its state writes.
    requestIdRef.current += 1
    setNarrativeText('')
    setDocuments([])
    setSuggestedName(null)
    setError(null)
    setIsGenerating(false)
    setIsSaving(false)
  }, [])

  return {
    isGenerating,
    isSaving,
    narrativeText,
    documents,
    suggestedName,
    error,
    generate,
    reroll,
    save,
    reset,
  }
}

// ── Thumbnail helper ──

export function getDocumentThumbnail (doc: PlaylistDocument): string | null {
  const refs = doc.content?.contentAssetReferences
  if (refs && refs.length > 0) {
    const enRef = refs.find((r) => r?.asset?.language === 'en') || refs[0]
    if (enRef?.asset?.renderUrl) return enRef.asset.renderUrl
  }
  return null
}
